import React, { Component, Fragment } from 'react';
import styled from 'styled-components';
import map from 'lodash/map';
import includes from 'lodash/includes';
import PropTypes from 'prop-types';

import media from '../../main-app/utils/mediaQueries';
import arrow_up from '../../main-app/images/arrow-up.svg';
import arrow_down from '../../main-app/images/arrow-down.svg';
import { NOTES_COLOR, TEXT_COLOR } from '../../main-app/theme';
import FaqSchema from './FAQSchema';

const ICON_OPEN_COLOR = '#54698d';

const PlusIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.23 12.923h5.078a.472.472 0 00.461-.461v-.924a.472.472 0 00-.461-.461H13.23c-.185 0-.308-.123-.308-.308V5.692a.472.472 0 00-.461-.461h-.924a.472.472 0 00-.461.461v5.077c0 .185-.123.308-.308.308H5.692a.472.472 0 00-.461.461v.924c0 .246.215.461.461.461h5.077c.185 0 .308.123.308.308v5.077c0 .246.215.461.461.461h.924a.472.472 0 00.461-.461V13.23c0-.185.123-.308.308-.308z"
      fill={TEXT_COLOR}
    />
  </svg>
);

const MinusIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4.615" y="11.077" width="14.769" height="1.846" rx="0.923" fill={ICON_OPEN_COLOR} />
  </svg>
);

const Container = styled.div`
  ${media.mobile`
    padding: 0 6px;
  `}
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: 0.2px;
  color: #354354;
  margin: 40px 0 12px;

  ${media.mobile`
    font-size: 18px;
    margin-top: 24px;
  `}
`;

const Block = styled.div`
  margin-bottom: 24px;
`;

const BlockTitle = styled.h3`
  font-size: ${(props) => props.questionFontSize};
  font-weight: bold;
  line-height: 1.5;
  letter-spacing: 0.27px;
  color: #354354;
  margin-top: 0;
  margin-bottom: 8px;
`;

const IconWrapper = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  margin-top: 2px;
`;

const BlockItem = styled.h4`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: ${(props) => props.questionFontSize};
  font-weight: 700;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.44;
  letter-spacing: 0.2px;
  margin: 0;
  padding: 8px 0;
  color: ${(props) => (props.isOpened ? NOTES_COLOR : TEXT_COLOR)};
  cursor: pointer;
  position: relative;
  ${media.mobile`
    padding-right: 25px;
    margin-bottom: 12px;

  `}
`;

const BlockInfo = styled.div`
  font-size: ${(props) => props.answerFontSize};
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.5;
  letter-spacing: 0.1px;
  padding: ${({ increasedPadding }) => (increasedPadding ? '20px 32px' : '16px 24px')};
  margin-bottom: 0;
  color: ${NOTES_COLOR};
  border-radius: 8px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
  background-color: ${(props) => props.answerBg};

  ${media.mobile`
    margin: 0 -8px;
  `}
`;

class FAQAccordion extends Component {
  constructor(props) {
    super(props);

    this.state = {
      visibleInfos: [],
    };
  }

  showInfoHandler = (id) => {
    const { visibleInfos } = this.state;
    const index = visibleInfos.indexOf(id);

    if (index > -1) {
      visibleInfos.splice(index, 1);
    } else {
      visibleInfos.push(id);
    }

    this.setState({
      visibleInfos,
    });
  };

  render() {
    const { visibleInfos } = this.state;
    const {
      faqList,
      answerBg = '#fff',
      questionFontSize = '16px',
      answerFontSize = '14px',
      increasedPadding = false,
      withSchema = true,
    } = this.props;

    return (
      <>
        {withSchema && <FaqSchema faqEntities={faqList.sections.map((section) => section.questions).flat(1)} />}
        <Container>
          {faqList.title && <Title>{faqList.title}</Title>}
          {map(faqList.sections, (section, index) => (
            <Block key={`faq-section${index}`}>
              {section.title && (
                <BlockTitle questionFontSize={questionFontSize}>{section.title}</BlockTitle>
              )}
              {map(section.questions, (item, yindex) => (
                <Fragment key={`faq-item-section${yindex}`}>
                  <BlockItem
                    isOpened={includes(visibleInfos, item.id)}
                    onClick={() => this.showInfoHandler(item.id)}
                    questionFontSize={questionFontSize}
                    id={`question${item.id}`}
                  >
                    <IconWrapper>
                      {includes(visibleInfos, item.id) ? <MinusIcon /> : <PlusIcon />}
                    </IconWrapper>
                    {item.question}
                  </BlockItem>
                  {includes(visibleInfos, item.id) && (
                    <BlockInfo
                      dangerouslySetInnerHTML={{ __html: item.answer }}
                      answerBg={answerBg}
                      answerFontSize={answerFontSize}
                      increasedPadding
                    />
                  )}
                </Fragment>
              ))}
            </Block>
          ))}
        </Container>
      </>
    );
  }
}

FAQAccordion.propTypes = {
  faqList: PropTypes.shape({
    title: PropTypes.string,
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        questions: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            question: PropTypes.string,
            answer: PropTypes.string,
          })
        ),
      })
    ),
  }).isRequired,
  answerBg: PropTypes.string,
  questionFontSize: PropTypes.string,
  withSchema: PropTypes.bool,
};

FAQAccordion.defaultProps = {
  answerBg: '#fff',
  questionFontSize: '16px',
};

export { FAQAccordion };
